<template>
    <layout-authentication-container>
        <div class="flex py-12 px-8 w-full max-w-lg items-center justify-between flex-col h-full rounded-xl">
            <div class="flex items-center justify-center w-full">
                <img src="@/assets/img/logo.png" @click="$router.push('/')" class="cursor-pointer h-[3.5rem]" alt="">
            </div>
            <div class="flex flex-col h-full gap-8 items-start w-full justify-center">
                <div class="flex flex-col gap-2 w-full items-center">
                    <span class="text-2xl text-gray-700 tracking-wide font-medium">Welcome Back</span>
                    <span class="text-xs md:text-sm text-center tracking-wide text-secondary">Please sign into your account
                        below</span>
                </div>
                <div class="flex flex-col gap-4 w-full">
                    <div class="w-full flex-col justify-start items-start">
                        <span class="text-sm text-secondary tracking-wide">Email or Username</span>
                        <a-input v-model:value="form.login" placeholder="Email Address, Username" size="large"
                            class="w-full mt-2" />
                        <span v-if="errors.login" class="text-red-500 text-xs mt-2 tracking-wide">{{ errors.login }}</span>
                    </div>
                    <div class="w-full flex-col justify-start items-start">
                        <span class="text-sm text-secondary tracking-wide">Password</span>
                        <a-input-password v-model:value="form.password" placeholder="Enter your password" type="password"
                            size="large" class="w-full mt-2" />
                        <span v-if="errors.password" class="text-red-500 text-xs mt-2 tracking-wide">{{ errors.password
                        }}</span>
                    </div>
                </div>
                <span @click="$router.push('/Forgot/Request')"
                    class="text-primary cursor-pointer text-sm font-normal tracking-wide">Forgot Password?</span>
                <a-button :loading="isLoading" @click="onClickSignin" class="w-full" type="primary" size="large">Sign
                    In</a-button>

                <div class="w-full justify-center items-center flex-col">
                    <div class="w-full justify-center gap-1 tracking-wide items-center flex">
                        <span class="text-sm text-secondary">Don't have an account?</span>
                        <span @click="$router.replace({ path: '/SignUp' });"
                            class="text-primary cursor-pointer text-sm font-normal">Register</span>
                    </div>

                    <div class="w-full justify-center gap-1 tracking-wide items-center flex mt-2">
                        <span class="text-sm text-secondary">Do you want to login as mentor?</span>
                        <span @click="$router.replace({ path: '/Mentor/SignIn' });"
                            class="text-primary cursor-pointer text-sm font-normal">Sign In</span>
                    </div>
                </div>
            </div>
        </div>
    </layout-authentication-container>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue"
import { message } from 'ant-design-vue';
import AuthApi from "../../api/auth"
import Cookies from 'js-cookie';
import useAuth from "@/composables/useAuth"
import { useOneSignal } from "@onesignal/onesignal-vue3";
import { useRouter } from 'vue-router';

const router = useRouter()

const isLoading = ref(false)
const authApi = new AuthApi()

const form = ref({
    login: '',
    password: ''
})

const errors = ref({
    login: '',
    password: ''
})

onMounted(() => {
    document.addEventListener('keypress', handleKeyPress)
})

onUnmounted(() => {
  document.removeEventListener('keypress', handleKeyPress)
})


const handleKeyPress = (event) => {
  if (event.key === 'Enter') {
    onClickSignin()
  }
}

// Watch for changes in the form to clear errors
watch(() => form.value.login, (newValue) => {
    if (newValue) {
        errors.value.login = '';
    }
});

watch(() => form.value.password, (newValue) => {
    if (newValue) {
        errors.value.password = '';
    }
});

// Validate form fields
const validateForm = () => {
    errors.value.login = form.value.login ? '' : 'Email or Username is required.'
    errors.value.password = form.value.password ? '' : 'Password is required.'
    return !errors.value.login && !errors.value.password
}

const getProfile = async (token) => {
    let success = false;
    let redirectTo = '/SignIn';

    try {
        const r = await authApi.getProfile()
        if (!r.data) {
            message.error("Failed to fetch profile")
            return;
        }
        useAuth().set(JSON.stringify(r.data), useAuth().get().token, true)

        redirectTo = sessionStorage.getItem('redirectAfterLogin') || '/Internal/Core';
        sessionStorage.removeItem('redirectAfterLogin');
        success = true;
    } catch (error) {
        console.error('Error during sign in (profile):', error);
        message.error(error?.response?.data?.message)
    }
    
    if (success) {
        try {
            const user = JSON.parse(JSON.parse(useAuth().get().user));
            
            await useOneSignal().login(user.email);

            // console.log('OneSignal login response:'), user.email;

            await useOneSignal().User.addTags({
                'type':  'startup', 
                'email': user.email, 
                'name': user.name
            });
            
        } catch (error) {
            console.error('Error during OneSignal setup:', error);
            // message.error(error?.response?.data?.message)   
        }

        router.replace({ path: redirectTo })
    }

    return success;
}

const onClickSignin = async () => {
    let successLogin = false;

    if (validateForm()) {
        isLoading.value = true
        try {
            const response = await authApi.login(form.value)
            if (!response) return false
            useAuth().set(null, response.data.access_token, false)  

            successLogin = true
        } catch (error) {
            
            const errorResponse = error?.response?.data;
            useAuth().set(null, errorResponse?.access_token, false)
            if (errorResponse.unverified === 1) {
                router.replace({ path: '/Verify/Email?pre=true' })
                return
            }
            // if (errorResponse.unverified === 2) {
            //     window.location.href = "/Verify/Whatsapp?pre=true"
            //     return
            // }

            console.error('Error during sign in:', errorResponse);
            message.error(errorResponse?.message)
        }
    }

    if (!successLogin) {
        isLoading.value = false
        return;
    };

    try {
        await getProfile();
    } catch (error) {
        console.error('Error during sign in (Get Profile):', error);
        message.error(error?.response?.data?.message ?? 'An error occurred. Please try again later.')
    } finally {
        isLoading.value = false
    }
}

</script>
